import {
  getQuickListItems,
  addListItem,
  toggleItem,
  deleteItem,
  updateItemQuantity,
  clearCheckedItems,
} from '../db/articles.js';
import { renderHeader } from '../components/header.js';
import { renderArticleItem } from '../components/article-item.js';
import { openModal } from '../components/modal.js';
import { createElement, clearElement, showToast } from '../utils/dom.js';
import { navigate } from '../router.js';
import { iconClipboardList, iconPlus, iconTrash } from '../utils/icons.js';

/**
 * Affiche la liste rapide (sans magasin ni rayons).
 * @param {HTMLElement} container
 */
export async function renderQuickList(container) {
  clearElement(container);

  try {
    const items = await getQuickListItems();
    const checkedCount = items.filter((i) => i.checked).length;
    const hasChecked = checkedCount > 0;
    const hasItems = items.length > 0;

    const pageClasses = [
      'page',
      'page-quick-list',
      'page--with-fab',
      'page--with-nav',
      hasItems ? 'page--with-bottom-bar' : '',
    ].filter(Boolean).join(' ');

    const page = createElement('div', { className: pageClasses });

    page.appendChild(renderHeader({
      title: 'Liste rapide',
      backPath: '/',
    }));

    const refresh = () => renderQuickList(container);

    const callbacks = {
      onToggle: async (id) => {
        try {
          await toggleItem(id);
          refresh();
        } catch (err) {
          showToast(err.message || 'Erreur', 3000, 'error');
        }
      },
      onDelete: async (id) => {
        try {
          await deleteItem(id);
          refresh();
        } catch (err) {
          showToast(err.message || 'Erreur', 3000, 'error');
        }
      },
      onQuantityChange: async (id, qty) => {
        try {
          await updateItemQuantity(id, qty);
          refresh();
        } catch (err) {
          showToast(err.message || 'Erreur', 3000, 'error');
        }
      },
    };

    if (items.length === 0) {
      page.appendChild(createElement('div', {
        className: 'empty-state',
        children: [
          createElement('span', { className: 'empty-state__icon', children: [iconClipboardList(48)] }),
          createElement('h2', { className: 'empty-state__title', text: 'Liste vide' }),
          createElement('p', {
            className: 'empty-state__message',
            text: 'Ajoutez des articles à votre liste rapide !',
          }),
        ],
      }));
    } else {
      const itemsContainer = createElement('div', { className: 'page-quick-list__items' });
      for (const item of items) {
        itemsContainer.appendChild(renderArticleItem(item, callbacks));
      }
      page.appendChild(itemsContainer);
    }

    container.appendChild(page);

    // FAB
    const fab = createElement('button', {
      className: 'btn btn--fab',
      attrs: { type: 'button', 'aria-label': 'Ajouter un article' },
      children: [iconPlus(24)],
      events: {
        click: () => openAddItemModal(refresh),
      },
    });
    container.appendChild(fab);

    // Barre d'actions
    if (hasItems) {
      const actionBar = createElement('div', { className: 'action-bar' });

      if (hasChecked) {
        actionBar.appendChild(createElement('button', {
          className: 'btn btn--ghost btn--danger-text',
          attrs: { type: 'button' },
          children: [iconTrash(16), ' Vider les cochés'],
          events: {
            click: async () => {
              try {
                await clearCheckedItems(null);
                refresh();
              } catch (err) {
                showToast(err.message || 'Erreur', 3000, 'error');
              }
            },
          },
        }));
      }

      actionBar.appendChild(createElement('span', {
        className: 'btn btn--ghost',
        attrs: { style: 'pointer-events: none; flex: 1; text-align: center;' },
        text: `${checkedCount}/${items.length} articles cochés`,
      }));

      container.appendChild(actionBar);
    }
  } catch (err) {
    showToast(err.message || 'Erreur lors du chargement', 3000, 'error');
    navigate('/');
  }
}

/**
 * Ouvre la modale d'ajout simplifiée (nom + quantité).
 * @param {Function} onRefresh
 */
function openAddItemModal(onRefresh) {
  const nameInput = createElement('input', {
    className: 'input',
    attrs: { type: 'text', placeholder: 'Nom de l\'article' },
  });

  const quantityInput = createElement('input', {
    className: 'input',
    attrs: { type: 'number', min: '1', value: '1' },
  });

  const form = createElement('div', {
    className: 'add-article-form',
    children: [
      createElement('div', {
        className: 'input-group',
        children: [
          createElement('label', { className: 'input-group__label', text: 'Nom de l\'article' }),
          nameInput,
        ],
      }),
      createElement('div', {
        className: 'input-group',
        children: [
          createElement('label', { className: 'input-group__label', text: 'Quantité' }),
          quantityInput,
        ],
      }),
    ],
  });

  const addBtn = createElement('button', {
    className: 'btn btn--primary btn--block',
    attrs: { type: 'button' },
    text: 'Ajouter',
    events: {
      click: async () => {
        try {
          const name = nameInput.value.trim();
          if (!name) {
            showToast('Le nom est requis', 3000, 'error');
            return;
          }

          const quantity = parseInt(quantityInput.value, 10) || 1;
          await addListItem({ storeId: null, name, quantity });
          onRefresh();

          nameInput.value = '';
          quantityInput.value = '1';
          nameInput.focus();
          showToast('Article ajouté', 1500, 'success');
        } catch (err) {
          showToast(err.message || 'Erreur', 3000, 'error');
        }
      },
    },
  });

  openModal({
    title: 'Ajouter un article',
    content: form,
    footer: addBtn,
  });

  nameInput.focus();
  nameInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') addBtn.click();
  });
}
